import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { withTranslation } from 'react-i18next';
import { FontsContext } from '../../context/fontsContext';
import styled from 'styled-components';
import FontSideBar from './FontSideBar';
import { Popover, Tooltip } from 'antd';
import { ButtonGroup } from 'react-rainbow-components';
import { SizeButton, SizeContainer, QlPickerContainer } from '../../artboard/Toolbar/styles';
import { TEXT_BLOCK_TOOLBAR } from '../../artboard/Toolbar/translationValues';
import {
  SmallToCapsIcon,
  CapsToSmallIcon,
  TextTransformIconSvg,
} from '../../images/TextBlockIcons';
import BackgroundFormat from './TextBlockToolbar/BackgroundFormat';
import ColorFormat from './TextBlockToolbar/ColorFormat';
import { RenderSpacing } from './TextBlockToolbar/LineFormat';
import { RenderBullets } from './TextBlockToolbar/ListFormat';
import ElementBackground from './TextBlockToolbar/ElementBackground';
import { sanitizeFontSize } from '../../utils/helper';
const NAMESPACES = ['TEXT_BLOCK_TOOLBAR'];

export const fontSizes = [
  '8px',
  '9px',
  '10px',
  '12px',
  '14px',
  '16px',
  '20px',
  '24px',
  '32px',
  '42px',
  '54px',
  '68px',
  '84px',
  '98px',
];

const ToolBarContainer = styled.div`
  .ql-snow.ql-toolbar {
    .ql-picker-options .ql-picker-item:before {
      content: attr(data-label);
    }

    .ql-custom-tray {
      gap: 6px;
      display: flex;
      align-items: center;
      justify-content: center;

      .ql-formats {
        margin-right: 4px;
      }

      svg {
        position: absolute;
        width: 22px !important;
        height: 22px !important;
      }
    }
    .ql-format-style {
      height: 30px;
      width: 30px;
      display: flex;
      align-item: center;
      justify-content: center;
      position: relative;

      &.ql-active {
        color: white;
        background: #454545;
        border-radius: 4px;

        .ql-fill {
          fill: white;
        }

        .ql-stroke {
          stroke: #fff;
        }
      }

      &[value = 'false'] {
        cursor: not-allowed;
        pointer-events: none;

        * {
          cursor: not-allowed;
          stroke: grey;
        }
      }

      ${QlPickerContainer} {
        position: absolute;
        top: 25px;
        left: 0;
      }
    }

    .ql-stroke {
      fill: none;
      stroke: #fff;
    }

    .ql-fill {
      fill: #fff;
      stroke: none;
    }

    .ql-picker {
      color: #fff;
    }

    button {
      + .ql-active .ql-stroke {
        stroke: white;
      }

      + .ql-active .ql-fill {
        fill: white;
      }

      :hover {
        .ql-stroke {
          stroke: grey;
        }

        .ql-fill {
          fill: grey;
        }
      }
    }
    .ql-picker-label {
      border: none;
      color: grey;
    }
    .ql-font.ql-picker.ql-expanded .ql-picker-options {
      height: 500px;
      overflow: scroll;
    }

    .hide-picker {
      .ql-picker-options {
        display: none;
      }
    }

    .ql-snow .ql-picker:not(.ql-color-picker):not(.ql-icon-picker) svg {
      color: grey;
    }

    .ql-picker.ql-expanded {
      .ql-picker-options {
        margin-top: 10px;
      }

      .ql-picker-label {
        border: none;
      }
    }
  }
  &.invisible {
    #format-link, #formats-transform {
      display: none !important;
    }
    .ql-custom-tray {
      margin: auto;
    }
  }
  &.visible {
    /* ----------- Non-Retina Screens ----------- */
  @media screen and (max-device-width: 1024px) {
    width: 220%;
    transform: scale(0.55);
    transform-origin: left;
  }
  @media screen and (min-device-width: 1025px) and (max-device-width: 1120px) {
    width: 195%;
    transform: scale(0.6);
    transform-origin: left;
  }
  @media screen and (min-device-width: 1121px) and (max-device-width: 1280px) {
    width: 165%;
    transform: scale(0.7);
    transform-origin: left;
  }
  @media screen and (min-device-width: 1281px) and (max-device-width: 1395px) {
    width: 125%;
    transform: scale(0.8);
    transform-origin: left;
  }
  }
  .sketch-picker {
    color: #333;
  }
`;

const TextBlockToolbar = ({
  isToolBarVisible,
  itemUUID,
  editorRef,
  enableSideBar,
  containerRef,
  isAbsolute,
  invisible,
  toolbarInvisible,
  isTocTitleBlock = false,
  isHTMLBlock = false,
  defaultStyles = undefined,
  t
}) => {
  const inputRef = useRef();
  const toolbarRef = useRef();
  const [fontListVisible, toggleFontList] = useState(false);
  const [forceRender, setForceRender] = useState(undefined);

  const { fontList } = useContext(FontsContext);

  const editor = editorRef?.current?.getEditor();
  const currentSelection = isToolBarVisible ? editor?.getSelection(invisible ? false : true) : undefined;

  let fontFamily = undefined;
  let boldEnabled = false;
  let italicEnabled = false;
  let boldApplied = false;
  let italicApplied = false;
  let linkApplied = false;
  if (isToolBarVisible && currentSelection) {
    if (isHTMLBlock) {
      const formats = editor?.getFormat(currentSelection);
      fontFamily = defaultStyles.fontFamily?.replaceAll('"', '');
      boldEnabled = fontList?.fontVarients?.find(font => font.name === fontFamily)?.variants['bold'];
      italicEnabled = fontList?.fontVarients?.find(font => font.name === fontFamily)?.variants['italic'];
      boldApplied = boldEnabled && formats['bold'];
      italicApplied = italicEnabled && formats['italic'];
      linkApplied = formats['link'];
    } else {
    const formats = editor?.getFormat(currentSelection);
    fontFamily = formats?.font || formats['block-font'];
    if (fontFamily && typeof fontFamily === 'string') {
      fontFamily = fontFamily?.replaceAll('"', '');
      boldEnabled = fontList?.fontVarients?.find(font => font.name === fontFamily)?.variants['bold'];
      italicEnabled = fontList?.fontVarients?.find(font => font.name === fontFamily)?.variants['italic'];
    }
    boldApplied = boldEnabled && formats['bold'];
    italicApplied = italicEnabled && formats['italic'];
    linkApplied = formats['link'];
    }
  }

  const setFontFamily = (newFontFamily) => {
    const focus = editor.root?.focus({
      preventScroll: true
    });
    editor.setSelection(currentSelection);
    const delta = editor.format('font', newFontFamily, 'user');
    setForceRender(currentValue => !currentValue);
  }

  const closeFontList = useCallback(() => {
    toggleFontList(false);
    enableSideBar(false);
    let editor = editorRef?.current?.getEditor();
    const scroll = editor.root?.focus({
      preventScroll: true
    });
  }, []);

  let fontSize = undefined;
  if (isToolBarVisible && currentSelection) {
    let currentSelectionFontSize = editor?.getFormat(currentSelection)?.size || editor?.getFormat(currentSelection)['block-size'];
    if (currentSelectionFontSize && typeof currentSelectionFontSize !== 'string') {
      currentSelectionFontSize = currentSelectionFontSize[currentSelectionFontSize.length - 1];
    }
    fontSize = parseFloat(parseFloat(currentSelectionFontSize?.replaceAll('px', '')).toFixed(2));
  }

  const setFontSize = (newFontSize) => {
    const focus = editor.root?.focus({
      preventScroll: true
    });
    editor.setSelection(currentSelection);
    editor.format('size', `${newFontSize}px`, 'user');
    setForceRender(currentValue => !currentValue);
  }

  const getDefaultFont = () => {
    if (editorRef) {
      let editor = editorRef?.current?.getEditor();
      let range = editor?.getSelection(true);
      let format;

      if (range) {
        format = editor?.getFormat(range);
      }

      return format && format.font ? format.font : '';
    }
  };

  const onFontClick = useCallback(
    (e) => {
      const editor = editorRef?.current?.getEditor();
      if (invisible) {
        editor.setSelection(0, editor.getLength());
      }
      toggleFontList(true);
      enableSideBar(true);
      editor.focus();
    },
    [invisible],
  );

  const onSizeChange = fontSizeVal => {
    if (!isNaN(fontSizeVal)) {
      setFontSize(fontSizeVal);
    }
  };

  const handleKeyDown = e => {
    e.stopPropagation();

    // Commit on Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      inputRef.current.blur();
      return;
    }

    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Tab',
    ];

    if (allowedKeys.includes(e.key)) return;

    if (!/^[0-9.]$/.test(e.key)) {
      e.preventDefault();
      return;
    }

    const el = inputRef.current;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const current = el.innerText || '';

    // caret offsets
    const start = range.startOffset;
    const end = range.endOffset;

    // 🔑 build next value using caret position
    const next =
      current.slice(0, start) +
      e.key +
      current.slice(end);

    const sanitized = sanitizeFontSize(next);

    e.preventDefault();

    if (!sanitized) return;

    el.innerText = sanitized;

    // 🔑 restore caret near where user typed
    requestAnimationFrame(() => {
      const newRange = document.createRange();
      const sel = window.getSelection();

      const caretPos = Math.min(
        start + 1,
        sanitized.length
      );

      newRange.setStart(el.firstChild || el, caretPos);
      newRange.collapse(true);

      sel.removeAllRanges();
      sel.addRange(newRange);
    });
  };


  const handlePaste = e => {
  e.preventDefault();
  const pasted = e.clipboardData.getData('text');
  const sanitized = sanitizeFontSize(pasted);
  document.execCommand('insertText', false, sanitized);
};


  const handleEditorUndoRedo = useCallback((e) => {
    const { detail } = e;
    const { action: changeType, style } = detail;
    const editor = editorRef?.current?.getEditor();
    if (containerRef && style) {
      containerRef.current = style;
    }
    if (!editor) return;
    if (changeType === 'undo') {
      editor.root.focus({ preventScroll: true });
      editor.history.customUndo();
    } else if (changeType === 'redo') {
      editor.root.focus({ preventScroll: true });
      editor.history.customRedo();
    }
  }, []);

  useEffect(() => {
    toolbarRef.current.addEventListener('handleEditorUndoRedo', handleEditorUndoRedo);
    return () => {
      toolbarRef.current.removeEventListener('handleEditorUndoRedo', handleEditorUndoRedo);
    }
  }, [handleEditorUndoRedo]);

  useEffect(() => {
    if (!isToolBarVisible) {
      toggleFontList(false);
    }
  }, [isToolBarVisible]);

  const renderSize = () => {
    return (
      <Tooltip
        placement="top"
        title={t(
          TEXT_BLOCK_TOOLBAR.FONT_SIZE_TOOLTIP.key,
          TEXT_BLOCK_TOOLBAR.FONT_SIZE_TOOLTIP.value,
        )}
      >
        <span>
          <ButtonGroup>
            <SizeButton
              comboBox={1}
              isMinus={1}
              style={{ borderRadius: 0 }}
              onClick={() => {
                if (!isNaN(fontSize)) {
                  onSizeChange(fontSize - 1);
                }
              }}
              icon="minus"
            />
            <Popover
              content={
                <SizeContainer>
                  <ul>
                    {fontSizes.map(data => (
                      <li
                        key={data}
                        onClick={e => {
                          onSizeChange(data?.replaceAll('px', ''));
                        }}
                      >
                        {data}
                      </li>
                    ))}
                  </ul>
                </SizeContainer>
              }
              title={null}
              trigger="click"
            >
              <div
                ref={inputRef}
                contentEditable={true}
                suppressContentEditableWarning={true}
                className="size-ref ql-size"
                style={{
                  width: '50px',
                  border: '1px solid white',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={() => {
                  const sanitized = sanitizeFontSize(inputRef.current.innerText.trim());
                  inputRef.current.innerText = sanitized;

                  const value = parseFloat(sanitized);
                  if (!isNaN(value) && value >= 1 && value <= 400 && value !== fontSize) {
                    onSizeChange(value);
                  }
                }}
              >
                {!isNaN(fontSize) && fontSize ? fontSize : ' '}
              </div>
            </Popover>
            <SizeButton
              comboBox={1}
              isMinus={1}
              style={{ borderRadius: 0 }}
              onClick={() => {
                if (!isNaN(fontSize)) {
                  onSizeChange(fontSize + 1);
                }
              }}
              icon="plus"
            />
          </ButtonGroup>
        </span>
      </Tooltip>
    );
  }

  const onToolbarFocus = useCallback(e => {
  if (invisible && !e.target?.closest('.size-ref')) {
    const editor = editorRef?.current?.getEditor();
    editor.setSelection(0, editor.getLength());
  }
}, [invisible, editorRef]);

  return (
    <ToolBarContainer className={`text-block-toolbar ${isToolBarVisible ? invisible ? 'invisible' : 'visible' : ''}`} onKeyDown={e => {
      e.stopPropagation();
    }} onMouseDown={e => e.stopPropagation()} onFocus={onToolbarFocus}
    >
      <div
        id={`toolbar-${itemUUID}`}
        ref={toolbarRef}
        style={{
          display: isToolBarVisible ? 'flex' : 'none',
          zIndex: 100,
          border: 'none',
          gap: '10px',          
          flexWrap: 'wrap',
          backgroundColor: 'rgb(37, 41, 45)',
          borderRadius: '10px',
        }}
        onKeyDown={e => {
          e.stopPropagation();
        }}
        onMouseDown={e => {
          if (!e.target?.closest('.size-ref')) {
             e.preventDefault();
          }
           e.stopPropagation();
        }}
      >
        {!isHTMLBlock && (<div
          className="ql-formats"
          style={{
            display: 'flex',
            gap: '10px',
            margin: '0',
          }}
        >
          <div
            class="ql-font"
            onMouseDown={onFontClick}
            style={{
              height: '32px',
              width: '150px',
              color: 'white',
              border: '1px solid white',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: '170px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#fff',
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              {fontFamily}{' '}
            </span>
          </div>
          <div class="">{renderSize()}</div>
        </div>)}

        <div className=" ql-custom-tray" style={{flexWrap: 'wrap'}}>
          <div className="ql-formats">
            <ColorFormat itemUUID={itemUUID} editorRef={editorRef} toolbar={'textBlockToolBar'} />
          </div>
          <div className="ql-formats">
            <BackgroundFormat itemUUID={itemUUID} editorRef={editorRef} toolbar={'textBlockToolBar'} />
          </div>

          <Tooltip
            placement="top"
            title={t(
              TEXT_BLOCK_TOOLBAR.TEXT_BOLD_TOOLTIP.key,
              TEXT_BLOCK_TOOLBAR.TEXT_BOLD_TOOLTIP.value,
            )}
          >
            <div className="ql-formats">
              <button class={`ql-bold  ql-format-style ${boldApplied && 'ql-active'}`} value={`${boldEnabled}`}></button>
            </div>
          </Tooltip>
          <Tooltip
            placement="top"
            title={t(
              TEXT_BLOCK_TOOLBAR.TEXT_ITALIC_TOOLTIP.key,
              TEXT_BLOCK_TOOLBAR.TEXT_ITALIC_TOOLTIP.value,
            )}
          >
            {' '}
            <div className="ql-formats">
              <button class={`ql-italic  ql-format-style ${italicApplied && 'ql-active'}`} value={`${italicEnabled}`}></button>
            </div>
          </Tooltip>
          <Tooltip
            placement="top"
            title={t(
              TEXT_BLOCK_TOOLBAR.TEXT_UNDERLINE_TOOLTIP.key,
              TEXT_BLOCK_TOOLBAR.TEXT_UNDERLINE_TOOLTIP.value,
            )}
          >
            <div className="ql-formats">
              <button class="ql-underline  ql-format-style"></button>
            </div>
          </Tooltip>
          {!isHTMLBlock && <Tooltip
            placement="top"
            title={t(
              TEXT_BLOCK_TOOLBAR.TEXT_STRIKE_THROUGH_TOOLTIP.key,
              TEXT_BLOCK_TOOLBAR.TEXT_STRIKE_THROUGH_TOOLTIP.value,
            )}
          >
            <div className="ql-formats">
              <button class="ql-strike  ql-format-style"></button>
            </div>
          </Tooltip>}
          {!isTocTitleBlock &&
          <Tooltip
            placement="top"
            title={t(
              TEXT_BLOCK_TOOLBAR.TEXT_LINK_TOOLTIP.key,
              TEXT_BLOCK_TOOLBAR.TEXT_LINK_TOOLTIP.value,
            )}
          >
            <div className="ql-formats" id="format-link">
              <button class={`ql-link  ql-format-style ${linkApplied && 'ql-active'}`} value={linkApplied}></button>
            </div>
          </Tooltip>
          }
          <Tooltip
            placement="top"
            title={t(
              TEXT_BLOCK_TOOLBAR.TEXT_TRANSFORM_TOOLTIP.key,
              TEXT_BLOCK_TOOLBAR.TEXT_TRANSFORM_TOOLTIP.value,
            )}
          >
            <div id={'formats-transform'} style={{ border: '1px solid grey', display: 'flex', borderRadius: '4px' }}>
              <button class="ql-textTransform ql-format-style" value="uppercase">
                <SmallToCapsIcon />
              </button>

              <button class="ql-textTransform ql-format-style" value="capitalize">
                <TextTransformIconSvg />
              </button>

              <button class="ql-textTransform ql-format-style" value="lowercase">
                <CapsToSmallIcon />
              </button>
            </div>
          </Tooltip>
          {!isHTMLBlock && (<div id={'formats-transform'} style={{ border: '1px solid grey', display: 'flex', borderRadius: '4px' }}>
            <Tooltip
              placement="top"
              title={t(
                TEXT_BLOCK_TOOLBAR.SUPERSCRIPT_TOOLTIP.key,
                TEXT_BLOCK_TOOLBAR.SUPERSCRIPT_TOOLTIP.value,
              )}
            >
              <div className="ql-formats" id="format-sup" style={{ marginRight: 0 }}>
                <button class="ql-script ql-format-style" value="super">
                </button>
              </div>
            </Tooltip>

            <Tooltip
              placement="top"
              title={t(
                TEXT_BLOCK_TOOLBAR.SUBSCRIPT_TOOLTIP.key,
                TEXT_BLOCK_TOOLBAR.SUBSCRIPT_TOOLTIP.value,
              )}
            >
              <div className="ql-formats" id="format-sub" style={{ marginRight: 0 }}>
                <button class="ql-script ql-format-style" value="sub">
                </button>
              </div>
            </Tooltip>
          </div>)}
          {!invisible && !isTocTitleBlock && <RenderBullets t={t} itemUUID={itemUUID} />}
          {!toolbarInvisible && <RenderSpacing t={t} itemUUID={itemUUID} isHTMLBlock={isHTMLBlock}/>}
          {!toolbarInvisible && <ElementBackground t={t} itemUUID={itemUUID} />}
        </div>
      </div>

      {fontListVisible &&(
        <FontSideBar
          itemUUID={itemUUID}
          editor={editorRef}
          setFontChange={setFontFamily}
          fontFamily={fontFamily}
          isToolBarVisible={fontListVisible}
          closeFontList={closeFontList}
          isAbsolute={isAbsolute}
        />
      )}
    </ToolBarContainer>
  );
};

export default withTranslation(NAMESPACES)(TextBlockToolbar);
